import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CustomPhoneInput } from "../Input";
import PortOne from "@portone/browser-sdk/v2";
import {
  PORTONE_CHANNEL_KEY,
  PORTONE_STORE_ID,
  PREMIUM_MANSEOL_PRICE,
} from "../../utils/constants";
import { getSaju } from "../../framework/api";
import { formatBirthDate, formatTime } from "../../utils/timeConvert";

interface Step2PaymentProps {
  value: {
    phoneNumber: string;
    email?: string;
    paymentMethod: "card" | "bank";
    accountInfo?: string;
  };
  onChange: (val: Step2PaymentProps["value"]) => void;
  onNext: (
    paymentMethod: "card" | "bank",
    value: string,
    extraPhone?: string
  ) => void;
  onBack: () => void;
  fortuneData: {
    name: string;
    birth: string;
    time: string;
    leap: string;
    gender: string;
    question: string;
  };
}

function Step2Payment({
  value,
  onChange,
  onNext,
  onBack,
  fortuneData,
}: Step2PaymentProps) {
  const location = useLocation();
  const isVideoRoute = location.pathname.includes("/video");

  const paymentAmount = PREMIUM_MANSEOL_PRICE; // 5만원

  // 핸드폰 번호 유효성 검사 (10~11자리 숫자)
  const isValidPhoneNumber = (num: string) => {
    return /^\d{11}$/.test(num);
  };
  // 이메일 유효성 검사
  const isValidEmail = (email: string) => {
    return /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(email);
  };

  const [privacyAgreement, setPrivacyAgreement] = React.useState(false);
  const [paymentAgreement, setPaymentAgreement] = React.useState(false);
  const [isPaying, setIsPaying] = React.useState(false);

  // 랜덤 결제 ID 생성
  function randomId() {
    // Array.from을 사용하여 다운레벨 환경에서도 반복 가능하게 처리
    return Array.from(crypto.getRandomValues(new Uint32Array(2)))
      .map((word) => word.toString(16).padStart(8, "0"))
      .join("");
  }

  // 포트원 나이스페이먼츠 카드결제 연동
  const handleNaverPay = async () => {
    // 휴대폰 번호 검증
    if (!isValidPhoneNumber(value.phoneNumber)) {
      alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
      return;
    }
    // 약관 동의 검증
    if (!privacyAgreement || !paymentAgreement) {
      alert("모든 약관에 동의해주세요.");
      return;
    }
    if (isVideoRoute) {
      if (!isValidEmail(value.email || "")) {
        alert("올바른 이메일 주소를 입력해주세요.");
        return;
      }
      if (!isValidPhoneNumber(value.phoneNumber)) {
        alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
        return;
      }
    } else {
      if (!isValidPhoneNumber(value.phoneNumber)) {
        alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
        return;
      }
    }
    setIsPaying(true);
    try {
      const paymentId = randomId();
      const orderName = isVideoRoute ? "사주 영상 상담" : "전화 사주 상담";

      // 모바일 환경 감지
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      const paymentRequest: any = {
        storeId: PORTONE_STORE_ID,
        channelKey: PORTONE_CHANNEL_KEY,
        paymentId,
        orderName,
        totalAmount: paymentAmount,
        currency: "CURRENCY_KRW",
        payMethod: "CARD",
        customData: {
          name: fortuneData.name,
          phone: value.phoneNumber,
          email: value.email,
          birth: fortuneData.birth,
          time: fortuneData.time,
          leap: fortuneData.leap,
          gender: fortuneData.gender,
          question: fortuneData.question,
        },
      };

      // 모바일인 경우 리디렉션 URL 추가
      if (isMobile) {
        const params = new URLSearchParams({
          productType: isVideoRoute ? "video" : "phone",
          name: fortuneData.name,
          phone: value.phoneNumber,
          email: value.email || "",
          birth: fortuneData.birth,
          time: fortuneData.time,
          leap: fortuneData.leap,
          gender: fortuneData.gender,
          question: fortuneData.question,
        });

        paymentRequest.redirectUrl =
          window.location.origin + "/payment/complete?" + params.toString();
      }

      const payment = await PortOne.requestPayment(paymentRequest);

      if (payment?.code !== undefined) {
        alert("결제 실패: " + payment?.message);
        setIsPaying(false);
        return;
      }
      console.log("payment success::", payment);
      // 여기서는 결제 성공하면 payment 정보랑 사용자가 입력한 정보를 서버에 전송해야함
      const combinedInfo = {
        ...payment,
        phone: value.phoneNumber,
        email: value.email || "",
        ...fortuneData,
      };
      console.log("payment + user info::", combinedInfo);

      const postData = {
        productType: isVideoRoute ? "video" : "phone",
        paymentType: "card",
        name: fortuneData.name,
        birthday: formatBirthDate(fortuneData.birth),
        birthdayType: fortuneData.leap.toUpperCase(),
        gender: fortuneData.gender.toUpperCase(),
        time: formatTime(fortuneData.time),
        phone: value.phoneNumber.replace(/[^0-9]/g, ""),
        question: fortuneData.question,
        paymentUid: payment?.paymentId,
        ...(isVideoRoute ? { email: value.email || "" } : {}),
        txId: payment?.txId,
        txType: "string",
      };

      await getSaju.post("/mobile/payment", postData);

      // 결제 성공 시 후처리 (예: 서버에 결제 완료 알림 등)
      // 여기서는 바로 onNext("naver", ...) 호출
      alert("결제 성공!");
      if (isVideoRoute) {
        onNext("card", value.email || "", value.phoneNumber);
      } else {
        onNext("card", value.phoneNumber);
      }
    } catch (err: any) {
      alert("결제 중 오류가 발생했습니다: " + (err?.message || err));
    } finally {
      setIsPaying(false);
    }
  };

  const handleBankTransfer = () => {
    // 휴대폰 번호 검증
    if (!isValidPhoneNumber(value.phoneNumber)) {
      alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
      return;
    }
    if (!privacyAgreement || !paymentAgreement) {
      alert("모든 약관에 동의해주세요.");
      return;
    }
    if (isVideoRoute) {
      if (!isValidEmail(value.email || "")) {
        alert("올바른 이메일 주소를 입력해주세요.");
        return;
      }
      if (!isValidPhoneNumber(value.phoneNumber)) {
        alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
        return;
      }
      onNext("bank", value.email || "", value.phoneNumber);
    } else {
      if (!isValidPhoneNumber(value.phoneNumber)) {
        alert("올바른 휴대폰 번호(10~11자리 숫자)를 입력해주세요.");
        return;
      }
      onNext("bank", value.phoneNumber);
    }
  };

  return (
    <main className="flex flex-col items-center bg-[#F9F9FC] relative">
      <button
        className="absolute -top-4 left-4 text-[#222] text-base"
        onClick={onBack}
      >
        뒤로가기
      </button>
      <h2 className="text-2xl font-bold mb-8">결제하기</h2>
      <div className="w-full max-w-[320px] flex flex-col gap-4">
        {/* 결제 금액 표시 */}
        <div className="p-6 flex items-center justify-between text-center w-full">
          <p className="text-2xl text-gray-600 mb-2">결제 금액</p>
          <p className="text-4xl font-extrabold text-[#222] tracking-wider mb-2">
            {paymentAmount.toLocaleString()}
            <span className="text-4xl font-extrabold">원</span>
          </p>
        </div>

        {/* 입력란: 이메일 or 휴대폰 */}
        {isVideoRoute && (
          <div className="flex flex-col gap-2">
            <label className="text-base font-extrabold">이메일 주소</label>
            <span className="text-sm text-gray-500 mb-1">
              사주 영상을 전송받을 이메일 주소를 작성해주세요.
            </span>
            <input
              type="email"
              name="email"
              placeholder="example@email.com"
              className="h-[48px] border border-[#D1C4E9] rounded-lg px-4 text-lg bg-white focus:outline-[#9D7FDE] placeholder:text-[#BDBDBD]"
              value={value.email || ""}
              onChange={(e) => onChange({ ...value, email: e.target.value })}
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label className="text-base font-extrabold">휴대폰 번호</label>
          <span className="text-sm text-gray-500 mb-1">
            전화 사주상담 시 연락 가능한 번호를 입력해주세요.
          </span>
          <CustomPhoneInput
            maxLength={13}
            type={"tel"}
            name={"phoneNumber"}
            placeholder="010-0000-0000"
            className="h-[48px] border border-[#D1C4E9] rounded-lg px-4 text-lg bg-white focus:outline-[#9D7FDE] placeholder:text-[#BDBDBD]"
            value={value.phoneNumber}
            onChange={(e) => {
              const numbersOnly = e.currentTarget.value.replace(/\D/g, "");
              onChange({ ...value, phoneNumber: numbersOnly });
            }}
          />
        </div>
        {/* 체크박스들 */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="privacy"
              checked={privacyAgreement}
              onChange={(e) => setPrivacyAgreement(e.target.checked)}
              className="w-5 h-5 accent-[#9D7FDE] border-gray-300 rounded focus:ring-[#9D7FDE]"
            />
            <label
              htmlFor="privacy"
              className="text-base text-[#222] select-none"
            >
              개인정보 이용 동의
              <Link
                target="_blank"
                to="/terms/pay/privacy"
                className="ml-2 text-sm  underline align-middle"
              >
                자세히 보기
              </Link>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="payment"
              checked={paymentAgreement}
              onChange={(e) => setPaymentAgreement(e.target.checked)}
              className="w-5 h-5 accent-[#9D7FDE] border-gray-300 rounded focus:ring-[#9D7FDE]"
            />
            <label
              htmlFor="payment"
              className="text-base text-[#222] select-none"
            >
              결제 진행 동의
              <Link
                target="_blank"
                to="/terms/pay/agreement"
                className="ml-2 text-sm  underline align-middle"
              >
                자세히 보기
              </Link>
            </label>
          </div>
        </div>

        {/* 결제 버튼들 */}
        <div className="flex flex-col gap-3 mt-2 font-[pretendard]">
          {/* 나이스 페이먼츠 SDK 결제 기능 추가 */}
          <button
            className="flex items-center justify-center gap-1 bg-[#9D7FDE] text-white text-xl font-bold rounded-lg h-[48px] w-full shadow-none hover:brightness-95 transition-all"
            onClick={handleNaverPay}
            type="button"
            disabled={isPaying}
          >
            <span>카드결제하기</span>
          </button>
          <button
            className="bg-[#F3EDFF] text-xl font-bold rounded-lg h-[48px] w-full border border-[#9D7FDE] hover:bg-[#ede6ff] transition-all"
            onClick={handleBankTransfer}
            type="button"
          >
            계좌이체하기
          </button>
        </div>
      </div>
    </main>
  );
}

export default Step2Payment;
